/** Escapes a search term for literal use in a regular expression. */
const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Wraps each case-insensitive occurrence of the terms in sanitised rich text in `<mark>`.
 *
 * Preconditions: The HTML is already sanitised; only its text is changed.
 * Postconditions: Only text nodes are searched, so tag names and attribute values are never
 * marked. Terms are matched literally, longer terms first so a shorter term inside a longer one
 * does not split it. Blank terms are ignored, and HTML without a match, or with no terms, is
 * returned unchanged.
 */
export const highlightRichText = (
    html: string,
    terms: readonly string[],
    markClassName?: string
) => {
    const searchTerms = [...new Set(terms.map(term => term.trim()).filter(Boolean))].sort(
        (left, right) => right.length - left.length
    );
    if (!html || searchTerms.length === 0) {
        return html;
    }

    const pattern = new RegExp(`(${searchTerms.map(escapeRegExp).join("|")})`, "gi");
    const document = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        textNodes.push(node as Text);
    }

    let marked = false;
    for (const textNode of textNodes) {
        const text = textNode.data;
        const parts = text.split(pattern);
        if (parts.length === 1) {
            continue;
        }

        marked = true;
        const fragment = document.createDocumentFragment();
        parts.forEach((part, index) => {
            if (!part) {
                return;
            }
            // Split with a capturing group places each match at an odd index.
            if (index % 2 === 1) {
                const mark = document.createElement("mark");
                if (markClassName) {
                    mark.className = markClassName;
                }
                mark.textContent = part;
                fragment.appendChild(mark);
            } else {
                fragment.appendChild(document.createTextNode(part));
            }
        });
        textNode.replaceWith(fragment);
    }

    return marked ? document.body.innerHTML : html;
};
